/**
 * React Query hooks for the "Explore More Areas" homepage module.
 * Fetches nearby areas, area facts, and rent-by-bedroom breakdowns.
 */

import { useQuery } from '@zg-rentals/react-query5';
import { area } from '@/src/lib/api/endpoints/area';
import type { AreaFactsByBedsData, AreaFactsData, NearbyArea } from '@/src/lib/api/endpoints/area';

export const exploreAreaKeys = {
  all: ['exploreAreas'] as const,
  nearby: (resourceId: string) => [...exploreAreaKeys.all, 'nearby', resourceId] as const,
  facts: (resourceId: string) => [...exploreAreaKeys.all, 'facts', resourceId] as const,
  factsByBeds: (resourceId: string) => [...exploreAreaKeys.all, 'factsByBeds', resourceId] as const,
};

export function useNearbyAreas(resourceId: string | undefined) {
  return useQuery<Array<NearbyArea>>({
    queryKey: exploreAreaKeys.nearby(resourceId ?? ''),
    queryFn: async () => {
      if (!resourceId) return [];
      const response = await area.nearby({ areaResourceId: resourceId, type: 'city', limit: 7 });
      return response.areas ?? [];
    },
    enabled: !!resourceId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useAreaFacts(resourceId: string | undefined) {
  return useQuery<AreaFactsData>({
    queryKey: exploreAreaKeys.facts(resourceId ?? ''),
    queryFn: () => area.facts(resourceId!),
    enabled: !!resourceId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useAreaFactsByBeds(resourceId: string | undefined) {
  return useQuery<AreaFactsByBedsData>({
    queryKey: exploreAreaKeys.factsByBeds(resourceId ?? ''),
    queryFn: () => area.factsByBeds(resourceId!),
    enabled: !!resourceId,
    staleTime: 10 * 60 * 1000,
  });
}
